import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { requireAdmin } from '@/lib/auth-guards'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ConfigDrawer } from '@/components/config-drawer'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { dashboardTabs, dashboardTopNav } from '@/features/dashboard'

export const Route = createFileRoute('/_authenticated/dashboard')({
  beforeLoad: requireAdmin,
  component: DashboardLayout,
})

function DashboardLayout() {
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin())
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const tabs = isSuperAdmin
    ? [
        dashboardTabs.overview,
        dashboardTabs.analytics,
        dashboardTabs.attendanceDetails,
        dashboardTabs.repeatedCheckIns,
        dashboardTabs.exitLimitCheckIns,
        dashboardTabs.plansAnalytics,
        dashboardTabs.membershipExpirations,
      ]
    : [
        dashboardTabs.analytics,
        dashboardTabs.repeatedCheckIns,
        dashboardTabs.exitLimitCheckIns,
      ]
  const activeTab =
    tabs.find((tab) => pathname.startsWith(tab.to))?.value ?? tabs[0].value

  return (
    <>
      <Header>
        <TopNav links={dashboardTopNav} />
        <div className='ms-auto flex items-center space-x-4'>
          <Search />
          <ThemeSwitch />
          <ConfigDrawer />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className='mb-2 flex items-center justify-between space-y-2'>
          <h1 className='text-2xl font-bold tracking-tight'>Dashboard</h1>
        </div>
        <Tabs orientation='vertical' value={activeTab} className='space-y-4'>
          <div className='w-full overflow-x-auto pb-2'>
            <TabsList>
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} asChild>
                  <Route.Link to={tab.to}>{tab.label}</Route.Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value={activeTab} className='space-y-4'>
            <Outlet />
          </TabsContent>
        </Tabs>
      </Main>
    </>
  )
}
